import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';

import { setAuthInterceptors } from './apis/rawApi';
import { AuthWrapper } from './components/Auth/AuthWrapper';
import { FloatingAssistantDock } from './components/Common/FloatingAssistantDock';
import { useAuth } from './hooks/useAuth';
import { useChatBubble } from './hooks/useChatBubble';
import NotFound from './pages/Basic/NotFound';
import { routes as appRoutes } from './routes';
import theme from './utils/theme';

setAuthInterceptors();

type AuthenticateGuardProps = {
  component: React.FC;
  requireAdmin?: boolean;
  requireAuth?: boolean;
};

const AuthenticateGuard = ({
  component,
  requireAdmin,
  requireAuth,
}: AuthenticateGuardProps) => {
  const { isLoading, isAuthenticated, isAdmin } = useAuth();

  if (isLoading) {
    return null;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/properties" replace />;
  }

  const Component = component;
  return <Component />;
};

const routerFutureFlags = {
  v7_relativeSplatPath: true,
} as const;

const AppRouter = () => {
  return (
    <Router future={routerFutureFlags}>
      <Routes>
        {appRoutes.map((route) => (
          <Route
            key={route.key}
            path={route.path}
            element={<route.component />}
          >
            {route.children.map((childRoute) => (
              <Route
                key={childRoute.key}
                path={childRoute.path}
                element={
                  childRoute.requireAuth ? (
                    <AuthenticateGuard
                      component={childRoute.component}
                      requireAdmin={childRoute.requireAdmin}
                      requireAuth={childRoute.requireAuth}
                    />
                  ) : (
                    <childRoute.component />
                  )
                }
              />
            ))}
          </Route>
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <FloatingAssistantDockWrapper />
    </Router>
  );
};

// Separate component that uses the hook inside Router context
const FloatingAssistantDockWrapper = () => {
  const { isVisible } = useChatBubble();

  if (!isVisible) return null;

  return <FloatingAssistantDock />;
};

export const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthWrapper>
        <AppRouter />
      </AuthWrapper>
    </ThemeProvider>
  );
};
